import _ from 'lodash';
import { AxiosResponse } from 'axios';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';

export interface TenantApiKey {
  _id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  isActive: boolean;
  rateLimit: number;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface CreateTenantApiKeyInput {
  name: string;
  scopes: string[];
  rateLimit?: number;
  expiresAt?: string;
}

export interface UpdateTenantApiKeyInput {
  name?: string;
  scopes?: string[];
  rateLimit?: number;
  expiresAt?: string | null;
  isActive?: boolean;
}

export const getTenantApiKeys = async () => {
  try {
    const response = await API_V1.get<{ keys: TenantApiKey[] }>('/api-keys');
    return response.data;
  } catch (e) {
    Logger.trace('Error while loading tenant API keys');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getTenantApiKeyScopes = async () => {
  try {
    const response = await API_V1.get<{ scopes: Record<string, string> }>('/api-keys/scopes');
    return response.data;
  } catch (e) {
    Logger.trace('Error while loading tenant API key scopes');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const createTenantApiKey = async (input: CreateTenantApiKeyInput) => {
  try {
    const response = await API_V1.post<{
      key: string;
      keyPrefix: string;
      name: string;
      scopes: string[];
      rateLimit: number;
      expiresAt: string | null;
    }>('/api-keys', input);
    return response.data;
  } catch (e) {
    Logger.trace('Error while creating tenant API key');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const updateTenantApiKey = async (id: string, input: UpdateTenantApiKeyInput) => {
  try {
    const response = await API_V1.patch<{ key: TenantApiKey }>(`/api-keys/${id}`, input);
    return response.data;
  } catch (e) {
    Logger.trace('Error while updating tenant API key %s', id);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const revokeTenantApiKey = async (id: string) => {
  try {
    const response = await API_V1.delete(`/api-keys/${id}`);
    return response.data;
  } catch (e) {
    Logger.trace('Error while revoking tenant API key %s', id);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getOwnOutlookApiKey = async (): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get('/api-keys/outlook');
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while getting own Outlook API Key');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};
